// Comandos dos agregados Responsável e Retirada Pessoal (issue #17).
//
// Dois agregados no mesmo arquivo porque a Retirada não existe sem o
// Responsável e as duas telas andam juntas; ainda assim são dois `agregado:`
// distintos no despachante, cada um com o seu Módulo.
//
// Formato do ADR-0013: os `validar…` devolvem os tipos do domínio, sem tipo
// espelho e sem `as`.

import type { Snapshot } from "../domain/snapshot.ts";
import {
  criarResponsavel,
  definirAtivoResponsavel,
  editarResponsavel,
  editarRetirada,
  excluirRetirada,
  registrarRetirada,
  type DadosResponsavel,
  type DadosRetirada,
} from "../domain/retiradas.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import { comoObjeto, exigirBooleano, exigirTexto, paraNumero, texto } from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

// --- Responsável -----------------------------------------------------------

export type ComandoResponsavel =
  | { tipo: "criar"; id: string; dados: DadosResponsavel }
  | { tipo: "editar"; id: string; dados: DadosResponsavel }
  | { tipo: "definirAtivo"; id: string; ativo: boolean };

export function executarComandoResponsavel(
  snapshot: Snapshot,
  cmd: ComandoResponsavel,
  _ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "criar":
      return criarResponsavel(snapshot, cmd.dados, cmd.id);
    case "editar":
      return editarResponsavel(snapshot, cmd.id, cmd.dados);
    case "definirAtivo":
      return definirAtivoResponsavel(snapshot, cmd.id, cmd.ativo);
    default:
      return assertNunca(cmd);
  }
}

function validarDadosResponsavel(valor: unknown): DadosResponsavel {
  const o = comoObjeto(valor, "Dados do responsável");
  return {
    nome: texto(o.nome),
    // ausente/null = sem limite; qualquer outra coisa vira número (NaN se
    // quebrado, e o domínio recusa) — a fronteira nunca inventa 0
    limiteMensalCentavos:
      o.limiteMensalCentavos == null ? null : paraNumero(o.limiteMensalCentavos),
  };
}

export function validarComandoResponsavel(valor: unknown): ComandoResponsavel {
  const c = comoObjeto(valor, "Comando de responsável");
  switch (c.tipo) {
    case "criar":
      return {
        tipo: "criar",
        id: exigirTexto(c.id, "id"),
        dados: validarDadosResponsavel(c.dados),
      };
    case "editar":
      return {
        tipo: "editar",
        id: exigirTexto(c.id, "id"),
        dados: validarDadosResponsavel(c.dados),
      };
    case "definirAtivo":
      return {
        tipo: "definirAtivo",
        id: exigirTexto(c.id, "id"),
        ativo: exigirBooleano(c.ativo, "ativo"),
      };
    default:
      throw new Error(
        `Tipo de comando de responsável desconhecido: ${String(c.tipo)}.`,
      );
  }
}

// --- Retirada Pessoal ------------------------------------------------------

export type ComandoRetirada =
  | { tipo: "registrar"; id: string; dados: DadosRetirada }
  | { tipo: "editar"; id: string; dados: DadosRetirada }
  | { tipo: "excluir"; id: string };

export function executarComandoRetirada(
  snapshot: Snapshot,
  cmd: ComandoRetirada,
  _ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "registrar":
      return registrarRetirada(snapshot, cmd.dados, cmd.id);
    case "editar":
      return editarRetirada(snapshot, cmd.id, cmd.dados);
    case "excluir":
      return excluirRetirada(snapshot, cmd.id);
    default:
      return assertNunca(cmd);
  }
}

function validarDadosRetirada(valor: unknown): DadosRetirada {
  const o = comoObjeto(valor, "Dados da retirada");
  return {
    responsavelId: texto(o.responsavelId),
    data: texto(o.data),
    valorCentavos: paraNumero(o.valorCentavos),
  };
}

export function validarComandoRetirada(valor: unknown): ComandoRetirada {
  const c = comoObjeto(valor, "Comando de retirada");
  switch (c.tipo) {
    case "registrar":
      return {
        tipo: "registrar",
        id: exigirTexto(c.id, "id"),
        dados: validarDadosRetirada(c.dados),
      };
    case "editar":
      return {
        tipo: "editar",
        id: exigirTexto(c.id, "id"),
        dados: validarDadosRetirada(c.dados),
      };
    case "excluir":
      return { tipo: "excluir", id: exigirTexto(c.id, "id") };
    default:
      throw new Error(`Tipo de comando de retirada desconhecido: ${String(c.tipo)}.`);
  }
}
